import React, { useEffect, useState, useContext, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  CircularProgress,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SyncIcon from '@mui/icons-material/Sync';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import IosShareIcon from '@mui/icons-material/IosShare';
import { AppContext } from './AppContext';

const baseURL = process.env.REACT_APP_API_BASE_URL;

const RecommendationView = () => {
  const { state: { chatTask } = {} } = useLocation();
  const navigate = useNavigate();
  const { isOnline, isWebSocketOpen, ws, handleFileUpload, setChatTask , waitForWebSocketOpen} = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [questionExpanded, setQuestionExpanded] = useState(null);
  const [questionText, setQuestionText] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const touchStartXRef = useRef(null);
  const headerRef = useRef(null);
  const scrollContainerRef = useRef(null); // added ref for scroll container
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);
  const questionRefs = useRef({});

  const updateDimensions = () => {
    if (headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      setHeaderHeight(headerRect.height);
      setScrollHeight(viewportHeight - headerRect.bottom);
    }
  };

  useLayoutEffect(() => {
    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(headerRef.current);
    window.addEventListener('resize', updateDimensions);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateDimensions);
    }
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateDimensions);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateDimensions);
      }
    };
  }, []);

  useEffect(() => {
    const selectionDetails = chatTask?.selectionDetail || [];
    if (!selectionDetails.length) return;

    setLoading(true);
    // Fetch recommendation entities
    fetch(`${baseURL}/utils/fetchClientTasks.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        function: 'fetchSelectionDetailEntities',
        recordIDs: selectionDetails,
      }),
    })
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(({ records = [] }) => {
        setRecs(records);
        if (records.length > 0) setExpanded([records[0].id]);
      })
      .catch(err => console.error('Fetch error:', err))
      .finally(() => setLoading(false));
  }, [chatTask]);

  // Fetch user-selected recommendations for this task
  useEffect(() => {
    const familyId = localStorage.getItem('familyID');
    const memberId = localStorage.getItem('memberID');
    if (!chatTask?.id || !familyId) return;

    const payload = {
      function: 'fetchSelectedEntities',
      TaskID: chatTask.id,
      FamilyID: familyId,
    };
    fetch(`${baseURL}/utils/handleRecommendationSelection.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(records => {
        // records is an array of selections; each record.SelectedEntities is an array
        const ids = records
        .filter(rec => rec.MemberID === memberId)
        .flatMap(rec => rec.SelectedEntities || []);
        setSelectedIds(ids);
      })
      .catch(err => console.error('Error fetching selected:', err));
  }, [chatTask]);

  useEffect(() => {
    updateDimensions();
  }, [questionExpanded]);

  const handleSelectClick = async (rec) => {
    try {
      const memberId = localStorage.getItem('memberID');
      const familyId = localStorage.getItem('familyID');
      const payload = {
        function: 'insertSelectedRecommendation',
        TaskID: chatTask?.id,
        FamilyID:familyId,
        MemberID: memberId,
        SelectedEntities: [rec.id],
        Notes: null,
      };

      const response = await fetch(`${baseURL}/utils/handleRecommendationSelection.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.success) {
        setSelectedIds(prev => [...prev, rec.id]);
        console.log('Recommendation recorded with ID:', result.id);
      } else {
        console.error('Error inserting recommendation:', result.error || result.message);
      }
    } catch (error) {
      console.error('Buy now error:', error);
    }
  };

  const handleBackClick = async () => {
    navigate('/');
    if (setChatTask) {
      await setChatTask(chatTask);
    }
    navigate('/chat');
  };

  const handleAccordionChange = panelId => (event, isExpanded) => {
    setExpanded(prev =>
      isExpanded
        ? [...prev, panelId]
        : prev.filter(id => id !== panelId)
    );
  };

  const toggleQuestion = id => {
    const newVal = questionExpanded === id ? null : id;
    setQuestionExpanded(newVal);
    setTimeout(() => {
      updateDimensions();
      if (newVal && questionRefs.current[newVal] && scrollContainerRef.current) {
        // Get the container and question element positions
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const elementRect = questionRefs.current[newVal].getBoundingClientRect();
        // Calculate offset: adjust with an extra margin (20px)
        const offset = elementRect.top - containerRect.top - 20;
        scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollTop + offset, behavior: 'smooth' });
      }
    }, 300);
  };

  const isCompletedItem = (statusText) => {
    return (statusText.includes("Done") || statusText.includes("Feedback/Payment"));
  };
  const isHoldItem = (chatTask) => {
    return chatTask?.isItemHold;
  };

  const sendMessage = async (rec) => {
    const message = questionText[rec.id] || '';

    try {
      const familyId = localStorage.getItem('familyID');
      const memberId = localStorage.getItem('memberID');

      let uploadedFile = null;
      if (rec.fields.Images?.length > 0) {
        const imageUrl = rec.fields.Images[0].url;
        const blob = await fetch(imageUrl).then(res => res.blob());
        const file = new File([blob], 'uploaded_image.jpg', { type: blob.type });
        uploadedFile = await handleFileUpload(file);
      }

      const messageData = {
        type: 'app_chat_message',
        member_id: memberId,
        family_id: familyId,
        task_id: chatTask?.id,
        secret: false,
        message,
        referencedMessage: null,
        uploaded_file: uploadedFile,
        file_metadata: null,
        recommendationData: {
          id: rec.id,
          Title: rec.fields.Title,
        }
      };

      if (isOnline) {
        await waitForWebSocketOpen()
        ws.send(JSON.stringify(messageData));
        setQuestionText(prev => ({ ...prev, [rec.id]: '' }));
        setQuestionExpanded(null);
      } else {
        console.log('Websocket is not open or offline');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchEndX - touchStartXRef.current;
    const swipeThreshold = -250; 
    if (deltaX < swipeThreshold) {
      handleBackClick();
    }
  };

  // Share logic
  const handleShare = (rec, websiteUrl) => {
    const shareData = {
      title: rec.fields.Title,
      url: websiteUrl,
      text:' Recommended by BlckBx, Personal Assistance for professionals & families',
     
    };
    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(websiteUrl);
      alert('Link copied to clipboard');
    } else {
      window.prompt('Copy this link:', websiteUrl);
    }
  };

  const statusText = chatTask?.fields?.['Task - Status Text'] || '';
  let progressValue = 0;
  if (isCompletedItem(statusText)) {
    progressValue = 100;
  } else {
    const progressField = chatTask?.fields?.["In Progress Percentage Rollup (from Task - Movement Log)"];
    if (progressField) {
      progressValue = progressField
        .split(",")
        .map((percent) => (parseFloat(percent.trim()) * 100).toFixed(0))
        .filter((percent) => !isNaN(percent))
        .pop() || 0;
      progressValue = Number(progressValue);
    }
  }

  const title = chatTask?.fields?.Title || 'Recommendation';
  const bannerText = chatTask?.fields?.['Recommendation Banner'] || 'Recommendations have been added, please confirm your preferred option.';

  return (
    <Box 
      sx={{ 
        position: 'fixed',
        overflow: 'hidden',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Fixed Header */}
      <Box 
        ref={headerRef}
        sx={{ 
          zIndex: 1100, 
          overflow: 'hidden' 
        }}
      >
        {/* Top Bar */}
        <Grid container alignItems="center">
          <Grid item xs={2}>
            <IconButton onClick={handleBackClick}>
              <ChevronLeftIcon sx={{ color: '#1C1D1F', fontSize: '1.8rem' }} />
            </IconButton>
          </Grid>
          <Grid item xs={8} textAlign="center"></Grid>
        </Grid>

        {/* Offline Notification */}
        {!isOnline && (
          <Box
            sx={{
              bgcolor: '#555556',
              color: '#F5F3F0',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2">
              You’re currently offline.
            </Typography>
          </Box>
        )}

        {/* Title, Progress, and Banner */}
        <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
            </Grid>
          </Grid>
          <Grid container spacing={1} alignItems="center">
            <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SyncIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {isHoldItem(chatTask)
                  ? ''
                  : (isCompletedItem(statusText) ? 'Completed ' : '') + `${progressValue}%`
                }
              </Typography>
            </Grid>
            {statusText && (
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isHoldItem(chatTask) ? (
                  <>
                    <PauseCircleOutlineIcon fontSize="small" sx={{ color: '#E9722F' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#E9722F' }}>
                      Paused
                    </Typography>
                  </>
                ) : statusText[0] === 'Info Needed' || statusText[0] === 'Info Needed from Client' ? (
                  <>
                    <PauseCircleOutlineIcon fontSize="small" sx={{ color: '#E9722F' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#E9722F' }}>
                      Waiting on you
                    </Typography>
                  </>
                ) : statusText[0] === 'Info Needed from Supplier' ? (
                  <>
                    <PauseCircleOutlineIcon fontSize="small" sx={{ color: '#E9722F' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#E9722F' }}>
                      Waiting on supplier
                    </Typography>
                  </>
                ) : (
                  <>
                    <TrendingUpIcon fontSize="small" sx={{ marginRight: '10px' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {statusText}
                    </Typography>
                  </>
                )}
              </Grid>
            )}
          </Grid>
          <Box sx={{ bgcolor: '#E0F7FA', px: 2, py: 1.5, my: 1, borderRadius: 1 }}>
            <Typography variant="body2">{bannerText}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box 
        ref={scrollContainerRef} 
        sx={{ 
          // mt: `${headerHeight}px`, 
          height: scrollHeight, // Use 100vh minus header height for full coverage
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain', 
          px: 2,
          pb: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress style={{ color: '#1C1D1F' }} />
          </Box>
        ) : (
          <Box>
            {recs.length > 0 ? (
              recs.map(rec => {
                const { id, fields } = rec;
                const { Title, Description, 'Description Final': descriptionFinal, Tags = [], 'Price range': priceRange, 'Price Final': priceFinal, Images = [], Website = [] } = fields;
                const imgUrl = Images[0]?.url;
                const imgFinalUrl = fields['Image Final']?.[0]?.url;
                const websiteUrl = Website[0] || '';
                const isSelected = selectedIds.includes(id);
                // const tagFinal= fields['Tags Final'] || [];
                
                return (
                  <Accordion key={id} expanded={expanded.includes(id)} onChange={handleAccordionChange(id)} elevation={0} sx={{ mb: 2 }}>
                    <AccordionSummary
                      disableGutters
                      sx={{
                        px: 2,
                        pb: 3,
                        minHeight: 'unset',
                        '&.Mui-expanded': { minHeight: 'unset' },
                        '& .MuiAccordionSummary-content': { my: 0, py: 0, margin: 0 },
                        '& .MuiAccordionSummary-content.Mui-expanded': { my: 0, py: 0, margin: 0 },
                      }}
                    >
                      <Box sx={{ width: '100%', pl: 2, pr:2 }}>
                        {(imgFinalUrl || imgUrl) && (
                          <Box sx={{ mt: 4, p: 0 }}>
                            <CardMedia
                              component="img"
                              image={imgFinalUrl || imgUrl}
                              alt={Title}
                              sx={{ borderRadius: 1, objectFit: 'contain', mt: 0, mb: 0 }}
                            />
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt:2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Title}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              aria-label="share"
                              sx={{ color: '#444444', }}
                              onClick={e => {
                                e.stopPropagation();
                                handleShare(rec, websiteUrl);
                              }}
                            >
                              <IosShareIcon sx={{ color: '#444444', }} />
                            </IconButton>
                            {expanded.includes(id) ? <RemoveIcon sx={{ color: '#444444', }} /> : <AddIcon sx={{ color: '#444444',}} />}
                          </Box>
                        </Box>
                        {(priceFinal || priceRange) && (
                          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                            {priceFinal || priceRange}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', pl: 4, pr:4, pt: 0, pb: 2 }}>
                      {Tags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Tags.map(tag => <Chip key={tag} label={tag} size="small" sx={{ bgcolor: '#E6CFC2', fontWeight: 500 }} />)}
                        </Box>
                      )}

                      {(descriptionFinal || Description)  && (
                        <Typography variant="body2" color="text.secondary">
                          {descriptionFinal || Description}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, mt: 2, mb:2 }}>
                        <Button component="a" href={websiteUrl} target="_blank" rel="noopener noreferrer" variant="outlined" fullWidth sx={{ textTransform: 'none', fontWeight: 500, color: 'black', borderColor: 'black', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', borderColor: 'black' } }}>More details</Button>
                        {isSelected ? (
                          <Button disabled  variant="contained" startIcon={<CheckIcon />} fullWidth sx={{ textTransform: 'none', fontWeight: 600,  }}>Selected</Button>
                        ) : (
                          <Button
                            variant="outlined"
                            fullWidth
                            sx={{ textTransform: 'none', color: 'black', borderColor: 'black' }}
                            onClick={() => handleSelectClick(rec)}
                          >
                            Select
                          </Button>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                        <Button
                          size="small"
                          startIcon={questionExpanded === id ? <RemoveIcon /> : <AddIcon />}
                          sx={{ textTransform: 'none', fontWeight: 500, color: 'black' }}
                          onClick={() => toggleQuestion(id)}
                        >
                          Ask a question
                        </Button>
                        {questionExpanded === id && (
                          <Box
                            ref={el => questionRefs.current[id] = el}
                            sx={{ 
                              width: '100%', 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: 1, 
                              alignItems: 'center',
                              mb: 2
                            }}
                          >
                            <TextField
                              multiline
                              rows={3}
                              fullWidth
                              placeholder="Type your question here"
                              value={questionText[id] || ''}
                              onChange={e => setQuestionText(prev => ({ ...prev, [id]: e.target.value }))}
                            />
                            <Button
                              variant="outlined"
                              fullWidth
                              sx={{ textTransform: 'none', color: 'black', borderColor: 'black' }}
                              onClick={() => sendMessage(rec)}
                              disabled={!questionText[id] || questionText[id].trim() === ''}
                            >
                              Send question
                            </Button>
                          </Box>
                        )}
                      </Box>

                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Typography>Failed to load recommendation</Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecommendationView;